import {Injectable} from "@angular/core";
import {Store} from "@ngrx/store";
import {IClinicalDashboardAndSelectedState} from "@ispm-client/modules/clinical-dashboard/store/reducers/clinicaldashboard.reducer";
import {CLINICALDASHBOARD_ACTIONS} from "@ispm-client/modules/clinical-dashboard/store/actions";
import {set, get, uniqBy} from 'lodash';
import {BundleCall} from "@ispm-client/modules/aggregator/services";
import {Observable} from "rxjs";
import {InterpolateService} from "@ispm/shared/services/interpolate.service";


@Injectable()
export class ClinicalDashboardService {
  public store$: Store<IClinicalDashboardAndSelectedState>;

  constructor(private store: Store<any>,
              private _bundleCall: BundleCall,
              private _interpolateService: InterpolateService) {
    this.store$ = store.select('clinicalDashboard') as any;
  }

  getFilterStore() {
    return this.store$.select('trendFilter');
  }

  /**
   * Update filter date ranges
   * @param dates
   * @param type
   */
  updateFilterStore(type, dates?: Date[]) {
    this.store$.dispatch({
      type: CLINICALDASHBOARD_ACTIONS.UPDATE_TREND_SELECTED_FILTER,
      payload: {dates: dates ? dates : this.getDateRanges(type), type: type}
    });
  }

  /**
   * Update trend groups
   * @param groups
   */
  updateGroupsStore(groups) {
    this.store$.dispatch({
      type: CLINICALDASHBOARD_ACTIONS.UPDATE_TREND_GROUPS,
      payload: groups
    });
  }


  /**
   * Clear trend groups
   * @param groups
   */
  clearGroupStore() {
    this.store$.dispatch({
      type: CLINICALDASHBOARD_ACTIONS.CLEAR_CLINICAL_TREND_GROUPS,
      payload: null
    });
  }

  filterGroupsAndStore(groups, selectedTimeFrame, selectedItems) {
    if (groups && Array.isArray(groups) && groups.length > 0) {
      for (let index in groups) {
        if (!isNaN(parseInt(index))) {
          let minDate;
          let maxDate;
          let group = groups[index];
          let data = get(group, 'data');
          let groupType = get(group, 'type');
          let labelPath = get(group, 'labelPath');
          let groupCodePath = `${labelPath}.${get(group, 'codePath')}`;
          let groupDatePath = `${get(group, 'datePath')}`;
          let groupUnitPath = `${get(group, 'mapping.widgetPayload.binding.unit.path')}`;
          let selectedGroupItems = get(selectedItems, groupType) || [];
          let selectedKeys = selectedGroupItems.map(item => item.itemKey) || [];
          if (selectedTimeFrame && selectedTimeFrame.length == 2) {
            minDate = selectedTimeFrame[0].getTime();
            maxDate = selectedTimeFrame[1].getTime();
          }
          if (selectedKeys && Array.isArray(selectedKeys) && selectedKeys.length > 0 && data) {
            data = data.filter(dataItem => {
              let hasItem = selectedKeys.includes(get(dataItem, groupCodePath));
              if (minDate && maxDate) {
                const dateOfCollection = new Date(get(dataItem, groupDatePath)).getTime();
                return hasItem && (dateOfCollection > minDate && dateOfCollection < maxDate);
              }
              return hasItem;
            });
          }
          else {
            data = [];
          }

          //Add start and end indexes for group based on filter selection
          if (minDate && maxDate && data.length > 0) {
            let uniqueData = uniqBy(data, `${groupCodePath}`);
            for (let uniqueItem of uniqueData) {
              let tempMinDate = {};
              set(tempMinDate, `${groupDatePath}`, minDate);
              set(tempMinDate, `${labelPath}`, get(uniqueItem, `${labelPath}`));
              set(tempMinDate, `${groupUnitPath}`, get(uniqueItem, `${groupUnitPath}`));
              data.push(tempMinDate);

              let tempMaxDate = {};
              set(tempMaxDate, `${groupDatePath}`, maxDate);
              set(tempMaxDate, `${labelPath}`, get(uniqueItem, `${labelPath}`));
              set(tempMaxDate, `${groupUnitPath}`, get(uniqueItem, `${groupUnitPath}`));
              data.push(tempMaxDate);
            }
          }

          set(group, 'data', data);
          groups.splice(parseInt(index), 1, group);
        }

      }
    }
    this.updateGroupsStore(groups);
  }

  /**
   * Get date ranges based on period
   * @param type
   */
  getDateRanges(type, initialDate?: Date, isIncreament?: boolean) {
    if (type == 'all') {
      return [];
    }
    let tempStartDate = initialDate ? initialDate : new Date();
    let currentDate = new Date(tempStartDate);
    let startDate = new Date(tempStartDate);
    switch (type) {
      case 'oneDay':
        if (isIncreament) {
          startDate = new Date(currentDate.getTime() + (24 * 60 * 60 * 1000));
        } else {
          startDate = new Date(currentDate.getTime() - (24 * 60 * 60 * 1000));
        }

        break;
      case 'oneWeek':
        if (isIncreament) {
          startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 7);
        } else {
          startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - 7);
        }

        break;
      case 'oneMonth':
        if (isIncreament) {
          startDate = new Date(currentDate.setMonth(currentDate.getMonth() + 1));
        } else {
          startDate = new Date(currentDate.setMonth(currentDate.getMonth() - 1));
        }

        break;
      case 'oneYear':
        if (isIncreament) {
          startDate = new Date(currentDate.setFullYear(currentDate.getFullYear() + 1));
        } else {
          startDate = new Date(currentDate.setFullYear(currentDate.getFullYear() - 1));
        }

        break;
    }
    if (isIncreament) {
      tempStartDate.setHours(0, 0, 0, 0);
      if(this.isToday(startDate)) {
        startDate.setHours(23, 59, 0, 0);
      }
      // if (type === 'oneMonth' || type === 'oneYear') {
      //   startDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() - 1);
      // }
      return [tempStartDate, startDate];
    }
    startDate.setHours(0, 0, 0, 0);
    if(this.isToday(tempStartDate)) {
      tempStartDate.setHours(23, 59, 0, 0);
    }
    // if (type === 'oneMonth' || type === 'oneYear') {
    //   tempStartDate = new Date(tempStartDate.getFullYear(), tempStartDate.getMonth(), tempStartDate.getDate() - 1);
    // }
    return [startDate, tempStartDate];
  }


  public getTrendsData(payload): Observable<any> {
    payload = payload.map(dataBinding => {
      set(dataBinding, 'queryString', this._interpolateService.interpolate(get(dataBinding, 'queryString'), this._bundleCall.getPatientContextForInterpolation()));
      return dataBinding;
    });
    return this._bundleCall.searchCall({"queries": payload});
  }

  public isToday(someDate): boolean {
    const today = new Date()
    return someDate.getDate() == today.getDate() &&
      someDate.getMonth() == today.getMonth() &&
      someDate.getFullYear() == today.getFullYear()
  }
}
